import React, {
  useEffect, useMemo, useState,
} from 'react';
import moment from 'moment';
import {
  Button, Drawer, Input, Tag, Tooltip, Typography
} from 'antd';
import {
  AppstoreOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CloseOutlined,
  CloseCircleFilled,
  DownOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  MinusCircleFilled,
  PictureOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'utils/detectDevice';
import type {
  EntityDrawerBlock,
  EntityDrawerData,
  EntityDrawerRanch,
  MonitoringSensorRow,
  RecommendationHealth,
  SensorStatus,
} from './EntityMonitoring.helpers';
import {
  RECOMMENDATION_HEALTH,
  getGatewayDisplayValue,
  NODE_TRANSPORT_TYPE,
  SENSOR_HEALTH_LABELS,
  SENSOR_STATUS,
} from './EntityMonitoring.helpers';

interface EntityMonitoringDrawerProps {
  visible: boolean;
  data: EntityDrawerData | null;
  snapshotDate: string;
  onClose: () => void;
}

interface CapabilityCardProps {
  icon: React.ReactNode;
  label: string;
  value?: string | boolean | null;
}

const getCapabilityValue = (value?: string | boolean | null) => {
  if (typeof value === 'boolean') return value ? 'Enabled' : 'Disabled';
  return value || 'Unknown';
};

const CapabilityCard: React.FC<CapabilityCardProps> = ({ icon, label, value }) => {
  const displayValue = getCapabilityValue(value);
  const isPositive = displayValue === 'Enabled'
    || displayValue === 'Active'
    || displayValue.toLowerCase() === 'full';

  return (
    <div className={`entity-monitoring-drawer__capability ${isPositive ? 'is-positive' : ''}`}>
      <span className="entity-monitoring-drawer__capability-icon">{icon}</span>
      <div>
        <Typography.Text className="entity-monitoring-drawer__capability-label">
          {label}
        </Typography.Text>
        <Typography.Text className="entity-monitoring-drawer__capability-value">
          {displayValue}
        </Typography.Text>
      </div>
    </div>
  );
};

CapabilityCard.defaultProps = {
  value: undefined,
};

const formatRecommendationTime = (value: string | null) => (
  value ? moment(value).format('MMM D, hh:mm A') : '-'
);

const RECOMMENDATION_HEALTH_PRESENTATION: Record<RecommendationHealth, {
  color: string;
  icon: React.ReactNode;
  label: string;
}> = {
  [RECOMMENDATION_HEALTH.CURRENT]: {
    color: 'green',
    icon: <CheckCircleFilled />,
    label: 'Generated recently',
  },
  [RECOMMENDATION_HEALTH.STALE]: {
    color: 'red',
    icon: <CloseCircleFilled />,
    label: 'Overdue',
  },
  [RECOMMENDATION_HEALTH.MISSING]: {
    color: 'orange',
    icon: <WarningFilled />,
    label: 'Not generated',
  },
  [RECOMMENDATION_HEALTH.DISABLED]: {
    color: 'default',
    icon: <MinusCircleFilled />,
    label: 'Not enabled',
  },
};

const SENSOR_STATUS_PRESENTATION: Record<SensorStatus, {
  color: string;
  label: string;
}> = {
  [SENSOR_STATUS.ONLINE]: {
    color: 'green', label: SENSOR_HEALTH_LABELS[SENSOR_STATUS.ONLINE]
  },
  [SENSOR_STATUS.DEGRADED]: {
    color: 'orange', label: SENSOR_HEALTH_LABELS[SENSOR_STATUS.DEGRADED]
  },
  [SENSOR_STATUS.OFFLINE]: {
    color: 'red', label: SENSOR_HEALTH_LABELS[SENSOR_STATUS.OFFLINE]
  },
  [SENSOR_STATUS.UNKNOWN]: {
    color: 'default', label: SENSOR_HEALTH_LABELS[SENSOR_STATUS.UNKNOWN]
  },
};

const getGatewayTooltip = (sensor: MonitoringSensorRow, gatewayValue: string) => {
  if (sensor.transportType === NODE_TRANSPORT_TYPE.CELLULAR) {
    return 'Cellular nodes connect directly and do not require a gateway.';
  }
  if (sensor.transportType === NODE_TRANSPORT_TYPE.LORA && gatewayValue === '0') {
    return 'This LoRa node requires a gateway, but none is associated.';
  }
  return undefined;
};

const SensorList: React.FC<{ sensors: MonitoringSensorRow[] }> = ({ sensors }) => (
  <div className="entity-monitoring-drawer__sensor-list">
    <div className="entity-monitoring-drawer__sensor-header">
      <Typography.Text>Sensor name</Typography.Text>
      <Typography.Text>Type</Typography.Text>
      <Typography.Text>Gateway</Typography.Text>
      <Typography.Text>Status</Typography.Text>
    </div>
    {sensors.length ? sensors.map((sensor) => {
      const presentation = SENSOR_STATUS_PRESENTATION[sensor.status];
      const showIdentifier = sensor.sensorIdentifier !== sensor.name;
      const gatewayValue = getGatewayDisplayValue(sensor);
      const gatewayTooltip = getGatewayTooltip(sensor, gatewayValue);
      return (
        <div key={sensor.id} className="entity-monitoring-drawer__sensor-row">
          <span className="entity-monitoring-drawer__sensor-identity">
            <strong>{sensor.name}</strong>
            {showIdentifier ? <small>{sensor.sensorIdentifier}</small> : null}
          </span>
          <Typography.Text className="entity-monitoring-drawer__sensor-type">
            {sensor.sensorType}
          </Typography.Text>
          <Tooltip title={gatewayTooltip}>
            <Typography.Text className="entity-monitoring-drawer__sensor-gateway">
              {gatewayValue}
            </Typography.Text>
          </Tooltip>
          <Tag color={presentation.color}>{presentation.label}</Tag>
        </div>
      );
    }) : (
      <div className="entity-monitoring-drawer__no-sensors">
        Sensor details are not available for this snapshot.
      </div>
    )}
  </div>
);

interface BlockSectionProps {
  block: EntityDrawerBlock;
  expanded: boolean;
  healthTooltip: string;
  onToggle: () => void;
}

const BlockSection: React.FC<BlockSectionProps> = ({
  block,
  expanded,
  healthTooltip,
  onToggle,
}) => {
  const presentation = RECOMMENDATION_HEALTH_PRESENTATION[block.recommendationHealth];
  return (
    <div className={`entity-monitoring-drawer__block-card is-${block.recommendationHealth}`}>
      <button
        type="button"
        className="entity-monitoring-drawer__block-summary"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="entity-monitoring-drawer__block-title">
          <strong>{block.name}</strong>
          <small>
            {block.sensors.length}
            {' '}
            {block.sensors.length === 1 ? 'sensor' : 'sensors'}
          </small>
        </span>
        <span className="entity-monitoring-drawer__block-status">
          <small>
            Recommendation
            {' '}
            <Tooltip
              title={healthTooltip}
              placement="bottom"
              overlayClassName="monitoring-status-tooltip"
              mouseEnterDelay={0.15}
              mouseLeaveDelay={0.1}
            >
              <InfoCircleOutlined onClick={(event) => event.stopPropagation()} />
            </Tooltip>
          </small>
          <Tag color={presentation.color} icon={presentation.icon}>
            {presentation.label}
          </Tag>
        </span>
        <span className="entity-monitoring-drawer__block-time">
          <small>Last recommendation</small>
          <span>{formatRecommendationTime(block.lastRecommendationTime)}</span>
        </span>
        {expanded ? <DownOutlined /> : <RightOutlined />}
      </button>
      {expanded ? <SensorList sensors={block.sensors} /> : null}
    </div>
  );
};

const getUnassignedBlockId = (ranchId: number) => `unassigned-${ranchId}`;

const getFirstBlockId = (ranch?: EntityDrawerRanch) => {
  if (!ranch) return null;
  const populatedBlock = ranch.blocks.find((block) => block.sensors.length);
  if (populatedBlock) return populatedBlock.id;
  if (ranch.unassignedSensors.length) return getUnassignedBlockId(ranch.id);
  return ranch.blocks[0]?.id || null;
};

const sensorMatchesSearch = (sensor: MonitoringSensorRow, query: string) => (
  sensor.name.toLowerCase().includes(query)
  || sensor.sensorIdentifier.toLowerCase().includes(query)
);

const EntityMonitoringDrawer: React.FC<EntityMonitoringDrawerProps> = ({
  visible,
  data,
  snapshotDate,
  onClose,
}) => {
  const { t } = useTranslation();
  const [activeRanchId, setActiveRanchId] = useState<number | null>(null);
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const [sensorSearch, setSensorSearch] = useState('');

  useEffect(() => {
    if (!visible) return;
    const firstRanch = data?.ranches[0];
    setActiveRanchId(firstRanch?.id || null);
    setActiveBlockId(getFirstBlockId(firstRanch));
    setSensorSearch('');
  }, [data, visible]);

  const searchQuery = sensorSearch.trim().toLowerCase();
  const visibleRanches = useMemo(() => {
    if (!data || !searchQuery) return data?.ranches || [];
    return data.ranches.reduce<EntityDrawerRanch[]>((ranches, ranch) => {
      const blocks = ranch.blocks.map((block) => ({
        ...block,
        sensors: block.sensors.filter((sensor) => sensorMatchesSearch(sensor, searchQuery)),
      })).filter((block) => block.sensors.length);
      const unassignedSensors = ranch.unassignedSensors.filter(
        (sensor) => sensorMatchesSearch(sensor, searchQuery),
      );
      if (blocks.length || unassignedSensors.length) {
        ranches.push({ ...ranch, blocks, unassignedSensors });
      }
      return ranches;
    }, []);
  }, [data, searchQuery]);

  const healthTooltip = t(
    'Green - Generated recently means no more than 2 days old. Red - Overdue means more than 2 days old. Amber - Not generated means no recommendation was found. Gray - Not enabled means recommendations are not configured for the block.',
  );

  const toggleRanch = (ranch: EntityDrawerRanch, expanded: boolean) => {
    setActiveRanchId(expanded ? null : ranch.id);
    setActiveBlockId(expanded ? null : getFirstBlockId(ranch));
  };

  return (
    <Drawer
      className="entity-monitoring-drawer"
      width={isMobile ? '100%' : 580}
      placement="right"
      visible={visible}
      onClose={onClose}
      destroyOnClose={false}
      closable={false}
      extra={(
        <Button
          type="text"
          className="entity-monitoring-drawer__close"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label={t('Close entity details')}
        />
      )}
      title={(
        <div className="entity-monitoring-drawer__title-group">
          <Typography.Title level={4}>{data?.name || t('Entity details')}</Typography.Title>
          <Typography.Text>
            {`${t('Monitoring date')}: ${moment(snapshotDate).format('MMM D, YYYY')}`}
          </Typography.Text>
        </div>
      )}
    >
      {data ? (
        <>
          <div className="entity-monitoring-drawer__capabilities">
            <CapabilityCard
              icon={<SafetyCertificateOutlined />}
              label={t('Subscription')}
              value={data.subscriptionType}
            />
            <CapabilityCard
              icon={<BulbOutlined />}
              label={t('Irrigation Recommendations')}
              value={data.irrigationRecommendationsEnabled}
            />
            <CapabilityCard
              icon={<PictureOutlined />}
              label={t('Imagery Recommendations')}
              value={data.imageryRecommendationsEnabled}
            />
            <CapabilityCard
              icon={<GlobalOutlined />}
              label={t('Satellite Imagery')}
              value={data.satelliteImageryActive ? 'Active' : data.satelliteImageryActive}
            />
          </div>

          <Input.Search
            className="entity-monitoring-drawer__sensor-search"
            value={sensorSearch}
            allowClear
            placeholder={t('Find sensor name or identifier')}
            onChange={(event) => setSensorSearch(event.target.value)}
          />

          <div className="entity-monitoring-drawer__section-heading">
            <Typography.Title level={5}>
              {t('Ranches')}
              {' '}
              (
              {data.ranches.length}
              )
            </Typography.Title>
          </div>

          <div className="entity-monitoring-drawer__ranches">
            {visibleRanches.map((ranch) => {
              const isExpanded = Boolean(searchQuery) || activeRanchId === ranch.id;
              return (
                <div key={ranch.id} className="entity-monitoring-drawer__ranch">
                  <button
                    type="button"
                    className="entity-monitoring-drawer__ranch-header"
                    onClick={() => toggleRanch(ranch, isExpanded)}
                    aria-expanded={isExpanded}
                  >
                    <span className="entity-monitoring-drawer__ranch-title">
                      <AppstoreOutlined />
                      <span>
                        <strong>{ranch.name}</strong>
                        <small>
                          {ranch.blocks.length}
                          {' '}
                          {t('blocks')}
                          {' '}
                          &middot;
                          {' '}
                          {ranch.sensors}
                          {' '}
                          {t('sensors')}
                        </small>
                      </span>
                    </span>
                    {isExpanded ? <DownOutlined /> : <RightOutlined />}
                  </button>

                  {isExpanded ? (
                    <div className="entity-monitoring-drawer__ranch-body">
                      {ranch.blocks.map((block) => {
                        const blockExpanded = Boolean(searchQuery) || activeBlockId === block.id;
                        return (
                          <BlockSection
                            key={block.id}
                            block={block}
                            expanded={blockExpanded}
                            healthTooltip={healthTooltip}
                            onToggle={() => setActiveBlockId(blockExpanded ? null : block.id)}
                          />
                        );
                      })}
                      {ranch.unassignedSensors.length ? (
                        <div className="entity-monitoring-drawer__block-card is-unassigned">
                          <button
                            type="button"
                            className="entity-monitoring-drawer__unassigned-summary"
                            onClick={() => {
                              const id = getUnassignedBlockId(ranch.id);
                              setActiveBlockId(activeBlockId === id ? null : id);
                            }}
                            aria-expanded={Boolean(searchQuery)
                              || activeBlockId === getUnassignedBlockId(ranch.id)}
                          >
                            <span className="entity-monitoring-drawer__block-title">
                              <strong>{t('Unassigned sensors')}</strong>
                              <small>
                                {ranch.unassignedSensors.length}
                                {' '}
                                {t('sensors')}
                              </small>
                            </span>
                            {Boolean(searchQuery)
                              || activeBlockId === getUnassignedBlockId(ranch.id)
                              ? <DownOutlined /> : <RightOutlined />}
                          </button>
                          {Boolean(searchQuery)
                            || activeBlockId === getUnassignedBlockId(ranch.id) ? (
                              <SensorList sensors={ranch.unassignedSensors} />
                            ) : null}
                        </div>
                      ) : null}
                      {!ranch.blocks.length && !ranch.unassignedSensors.length ? (
                        <div className="entity-monitoring-drawer__no-blocks">
                          {t('No blocks or sensor details are available for this snapshot.')}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {searchQuery && !visibleRanches.length ? (
              <div className="entity-monitoring-drawer__no-results">
                {t('No sensors match this name or identifier.')}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </Drawer>
  );
};

export default EntityMonitoringDrawer;
